export const ECMWF_IMPORT_TEMPLATE_URL =
    `${process.env.PUBLIC_URL || ''}/templates/import_ecmwf_v2_scheduled_forecast_template.xlsx`;

export const ECMWF_IMPORT_ROW_STATUS = {
    VALID: 'valid',
    MATCHED: 'matched',
    CREATED: 'created',
    UPDATED: 'updated',
    NO_MATCH: 'no_matching_policy',
    AMBIGUOUS_LOCATION: 'ambiguous_location',
    DUPLICATE_TARGET: 'duplicate_import_target',
    AMBIGUOUS_FORECAST: 'ambiguous_existing_forecast',
    UPSERT_ERROR: 'upsert_error',
};

export const validateEcmwfImportFile = (file) => {
    if (!file) return { valid: false, message: 'Veuillez sélectionner un fichier Excel.' };
    if (!/\.xlsx$/i.test(file.name || '')) {
        return { valid: false, message: 'Seuls les fichiers Excel .xlsx sont acceptés.' };
    }
    return { valid: true, message: null };
};

export const getEcmwfImportErrorMessage = (error) => {
    const data = error?.response?.data;
    const detail = data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((item) => item?.msg || item?.message || String(item))
            .join(', ');
    }
    if (detail && typeof detail === 'object') {
        return detail.message || detail.reason || JSON.stringify(detail);
    }
    if (typeof data?.message === 'string' && data.message.trim()) return data.message;

    const status = error?.response?.status;
    if (status === 401) return 'Votre session a expiré. Veuillez vous reconnecter.';
    if (status === 403) return 'Cette fonctionnalité est réservée aux administrateurs.';
    if (status === 422) return 'Requête invalide : le champ fichier est absent ou incorrect.';
    if (status === 500) return 'Erreur serveur. Vous pouvez réessayer.';
    return error?.message || 'Une erreur est survenue pendant l’import.';
};

export const getImportDetailRows = (result) => {
    if (!result?.details) return [];
    const hasProcessedRows = Array.isArray(result.details.processed_rows);
    const groups = hasProcessedRows
        ? [result.details.processed_rows, result.details.invalid_rows]
        : [
            result.details.matched_rows,
            result.details.valid_rows,
            result.details.invalid_rows,
        ];
    const seen = new Set();
    return groups.flatMap((rows) => (Array.isArray(rows) ? rows : []))
        .filter((row) => {
            const key = [
                row.excel_row ?? '',
                row.status ?? '',
                row.match?.policy_id ?? '',
                row.match?.activity_id ?? '',
                row.action ?? '',
            ].join(':');
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .sort((a, b) => Number(a.excel_row || 0) - Number(b.excel_row || 0));
};

export const getDryRunProcessableCount = (result) => {
    const summary = result?.summary || {};
    const explicit = summary.rows_matched ?? summary.rows_valid;
    if (explicit != null) return Number(explicit) || 0;
    return getImportDetailRows(result)
        .filter((row) => ['valid', 'matched'].includes(row.status))
        .length;
};

export const canConfirmEcmwfImport = ({
    dryRunResult,
    validatedFile,
    selectedFile,
    loading,
}) => Boolean(
    dryRunResult
    && dryRunResult.dry_run === true
    && (
        Number(dryRunResult.status_code) === 200
        || String(dryRunResult.status || '').toLowerCase() === 'success'
    )
    && getDryRunProcessableCount(dryRunResult) > 0
    && validatedFile
    && selectedFile === validatedFile
    && !loading
);

export const normalizeAdminRole = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const decodeJwtPayload = (token) => {
    try {
        const payload = token?.split('.')?.[1];
        if (!payload) return null;
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        const decoded = decodeURIComponent(
            window.atob(normalized)
                .split('')
                .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
                .join('')
        );
        return JSON.parse(decoded);
    } catch {
        return null;
    }
};

export const getAdminRoleFromToken = (token) => {
    const payload = decodeJwtPayload(token);
    const candidate = payload?.role
        || payload?.user_role
        || payload?.role_name
        || payload?.roles?.[0]
        || null;
    return normalizeAdminRole(candidate);
};

export const isAdministratorRole = (role) => (
    ['admin', 'administrator', 'administrateur'].includes(normalizeAdminRole(role))
);

/** Rôles autorisés pour POST /api/standard/trips/scheduled-forecast/import-excel */
export const canRunStandardScheduledForecastImport = (role) => {
    const normalized = normalizeAdminRole(role);
    return (
        isAdministratorRole(normalized)
        || normalized === 'restricted_admin'
    );
};

export const getStandardScheduledForecastImportErrorMessage = (error) => {
    const data = error?.response?.data;
    const detail = data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((item) => item?.msg || item?.message || String(item))
            .join(', ');
    }
    if (detail && typeof detail === 'object') {
        return detail.message || detail.reason || JSON.stringify(detail);
    }
    if (typeof data?.message === 'string' && data.message.trim()) return data.message;

    const status = error?.response?.status;
    if (status === 400) {
        return 'Fichier Excel invalide ou colonnes manquantes (data_source requis : ECMWF, METEOMATICS).';
    }
    if (status === 401) return 'Votre session a expiré. Veuillez vous reconnecter.';
    if (status === 403) {
        return 'Cette fonctionnalité est réservée aux rôles Administrator et Restricted Admin.';
    }
    if (status === 422) return 'Requête invalide : le champ fichier est absent ou incorrect.';
    if (status === 500) return 'Erreur serveur. Vous pouvez réessayer.';
    return error?.message || 'Une erreur est survenue pendant l’import Standard.';
};

